import { useEffect, useMemo, useState } from "react"
import fp from "lodash/fp"
import { useSelector } from "react-redux"

import type { Span } from "types/spans.types"

import { selectCurrentOrgId } from "features/store/orgSlice"
import { useLazyPowerlineSpansQuery } from "features/api"
import { fetchAllPowerlineSpans } from "helpers/utils/spans"

interface UseAllPowerlineSpansProps {
  powerlineId: number | undefined
}

export const useAllPowerlineSpans = ({ powerlineId }: UseAllPowerlineSpansProps) => {
  const orgId = useSelector(selectCurrentOrgId)
  const [allSpans, setAllSpans] = useState<Span[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<any>(null)
  const [getPowerlineSpans] = useLazyPowerlineSpansQuery()

  useEffect(() => {
    if (!orgId || !powerlineId) {
      setAllSpans([])
      setLoading(false)
      setError(null)
      return
    }
    let isMounted = true
    const fetchAll = async () => {
      setLoading(true)
      setError(null)
      try {
        const results = await fetchAllPowerlineSpans(
          getPowerlineSpans,
          Number(powerlineId),
          Number(orgId),
        )
        if (isMounted) {
          setAllSpans(results)
        }
      } catch (err) {
        if (isMounted) setError(err)
      } finally {
        if (isMounted) setLoading(false)
      }
    }
    fetchAll()
    return () => {
      isMounted = false
    }
  }, [orgId, powerlineId, getPowerlineSpans])

  const spansById = useMemo(() => {
    return fp.keyBy((s: Span) => s.id, allSpans)
  }, [allSpans])

  const spansByTowerPair = useMemo(() => {
    const map: Record<string, Span[]> = {}
    for (const span of allSpans) {
      if (span.tower_ids?.length === 2) {
        const [towerAId, towerBId] = span.tower_ids
        const key = `${Math.min(towerAId, towerBId)}-${Math.max(towerAId, towerBId)}`
        if (!map[key]) {
          map[key] = []
        }
        map[key].push(span)
      }
    }
    return map
  }, [allSpans])

  return {
    spans: allSpans,
    spansById,
    spansByTowerPair,
    isLoading: loading,
    isError: error,
  }
}
